import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import { Subject, takeUntil, combineLatest, timer, BehaviorSubject } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import {NgClass, NgIf} from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { FeatMgmtService } from "../../../features/admin/components/feat-mgmt/feat-mgmt.service";
import { AppResourceService } from "../../../features/admin/components/feat-mgmt/app-resource.service";
import {AppResource, FeatModule, ResourceType} from "../../../features/admin/components/feat-mgmt/feat-mgmt.types";
import {MatButton} from "@angular/material/button";
import {RouterLink} from "@angular/router";

@Component({
    selector: 'expire-banner',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './expire.banner.component.html',
    styles: `
        .fade-in {
          animation: fadeIn 0.3s ease-in forwards;
        }

        .fade-out {
          animation: fadeOut 0.3s ease-out forwards;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

    `,
    standalone: true,
    imports: [
        NgClass,
        MatIcon,
        NgIf,
        MatButton,
        RouterLink
    ]
})
export class ExpireBannerComponent implements OnInit, OnDestroy {

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _messageIndex: number = 0; // Tracks index in bannerMessages
    private _bannerMessages$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    private _cyclingMessages: boolean = false;

    bannerVisible: boolean = false;
    bannerContent: string = "";
    bannerMessages: string[] = [];

    /**
     * Constructor
     */
    constructor(
        private _featMgmtService: FeatMgmtService,
        private _resourceMgmtService: AppResourceService,
        private readonly _cdref: ChangeDetectorRef,
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to both services and trigger message updates
        combineLatest([
            this._featMgmtService.data$,
            this._resourceMgmtService.data$
        ])
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(([featData, resourceData]) => {
                // Update `bannerMessages` dynamically based on fetched data
                const modules: FeatModule[] = this._featMgmtService.getModulesThatExpiresSoonOrExpired();
                if(modules?.length > 0) {
                    this.bannerMessages = modules.map(module =>
                        `Accesul la modulul '${module.name}' expiră in ${this._featMgmtService.getTimeUntilExpirationAsString(module)}`
                    );
                }

                const lowResources:AppResource[] = this._resourceMgmtService.getResourcesThatAreLow();
                let resourceMsgs:string [] = [];
                if(lowResources?.length > 0) {
                    lowResources.forEach(resource => {
                        if(resource.resourceType == ResourceType.SMS && resource.availableResourceCount < 10) {
                            resourceMsgs.push(`Nr. '${resource.resourceType}' ramase: ${resource.availableResourceCount}.`);
                            resourceMsgs.push(`Trimiterea automata a SMS-urilor se DEZACTIVAZA.`);
                            resourceMsgs.push(`Vă rugăm reîncarcați SMS in cel mai scurt timp.`);
                        }else{
                            resourceMsgs.push(`'${resource.resourceType}' sunt pe terminate. Au mai rămas doar ${resource.availableResourceCount}.`);
                        }
                    })
                }
                this.bannerMessages = [...this.bannerMessages, ...resourceMsgs];

                // Pass the new bannerMessages to BehaviorSubject
                this._bannerMessages$.next(this.bannerMessages);
                this._cdref.markForCheck();
            });

        // Start cycling through messages in an infinite loop
        this._startMessageCycling();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        this._bannerMessages$.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Starts cycling through the `bannerMessages` array
     */
    private _startMessageCycling(): void {
        if (this._cyclingMessages) {
            return; // Prevent duplicate loops
        }

        this._cyclingMessages = true;

        this._bannerMessages$
            .pipe(
                switchMap((messages) =>
                    timer(0, 5000).pipe( // Switch to a timer running every 5 seconds
                        takeWhile(() => this._cyclingMessages), // Stop the loop when cycling is disabled
                        takeWhile(() => messages.length > 0), // Ensure we only loop if messages are available
                    )
                ),
                takeUntil(this._unsubscribeAll) // Unsubscribe when the component is destroyed
            )
            .subscribe(() => {
                if (this.bannerMessages.length > 0) {
                    // Display the current message and cycle to the next
                    this.bannerVisible = false; // Trigger fade-out
                    this._cdref.markForCheck();

                    setTimeout(() => {
                        this.bannerContent = this.bannerMessages[this._messageIndex];
                    }, 500); // update text while not visible

                    setTimeout(() => {
                        this.bannerVisible = true; // Trigger fade-in
                        this._cdref.markForCheck();
                    }, 1000); // Adjust this timing to match the fade-out animation duration

                    this._messageIndex = (this._messageIndex + 1) % this.bannerMessages.length; // Cycle to the next index
                }
            });
    }
}
